import { addDays, startOfDay } from "date-fns";
import type {
  AnalyticsChannelOption,
  AnalyticsDateRange,
  AnalyticsFilters,
} from "@/features/analytics/types";
import type { DeviceListRequest } from "./types";

// Canonical platform names as stored in `vast.platform`. The filter sends a
// picked name verbatim; the resolver normalises arbitrary casing back to the
// canonical spelling for display.
const PLATFORM_NAMES = [
  "Volia",
  "Triolan",
  "YouTv",
  "Olltv",
  "Kyivstar",
  "OmegaTv",
  "KoloTv",
  "Ipnet",
  "ProstoTv",
  "LiveTv",
  "UkrTv",
  "OttClub",
  "SweetTV",
] as const;

const PLATFORM_BY_LOWERCASE = (() => {
  const map = new Map<string, string>();
  for (const name of PLATFORM_NAMES) map.set(name.toLowerCase(), name);
  return map;
})();

export const resolvePlatformName = (
  value: string | null | undefined,
): string => {
  if (!value) return "—";
  return PLATFORM_BY_LOWERCASE.get(value.toLowerCase()) ?? value;
};

export const PLATFORM_FILTER_OPTIONS: Array<{ value: string; label: string }> =
  [...PLATFORM_NAMES]
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({ value: name, label: name }));

const VAST_KIND_LABELS: Record<number, string> = {
  0: "Dynamic",
  1: "Overlay",
  2: "Banner",
  3: "Static",
};

export const resolveVastKind = (kind: number | null | undefined): string => {
  if (kind == null) return "—";
  return VAST_KIND_LABELS[kind] ?? String(kind);
};

export const buildChannelLookup = (
  channels: AnalyticsChannelOption[],
): Map<number, AnalyticsChannelOption> => {
  const map = new Map<number, AnalyticsChannelOption>();
  for (const channel of channels) map.set(channel.id, channel);
  return map;
};

export const resolveChannelName = (
  channelLookup: Map<number, AnalyticsChannelOption>,
  channelId: number | null | undefined,
): string => {
  if (channelId == null) return "—";
  return channelLookup.get(channelId)?.name ?? String(channelId);
};

type PickerRange = { from: Date; to: Date };

type DevicesUrlState = {
  filters: AnalyticsFilters;
  range: PickerRange | null;
  page: number;
};

const STRING_FILTER_KEYS = [
  "country",
  "region",
  "city",
  "platform",
  "categories",
  "campaignUuid",
] as const satisfies ReadonlyArray<
  keyof Pick<
    AnalyticsFilters,
    "country" | "region" | "city" | "platform" | "categories" | "campaignUuid"
  >
>;

const NUMBER_FILTER_KEYS = [
  "deviceType",
  "channelId",
  "kind",
] as const satisfies ReadonlyArray<
  keyof Pick<AnalyticsFilters, "deviceType" | "channelId" | "kind">
>;

const parseList = (raw: string | null): string[] =>
  raw ? raw.split(",").filter(Boolean) : [];

const parseNumberList = (raw: string | null): number[] =>
  parseList(raw)
    .map((v) => Number(v))
    .filter((n) => !Number.isNaN(n));

const parseDate = (raw: string | null): Date | null => {
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const parseDevicesUrlState = (
  search: URLSearchParams,
): DevicesUrlState => {
  const filters: AnalyticsFilters = {
    country: parseList(search.get("country")),
    region: parseList(search.get("region")),
    city: parseList(search.get("city")),
    platform: parseList(search.get("platform")),
    categories: parseList(search.get("categories")),
    campaignUuid: parseList(search.get("campaignUuid")),
    deviceType: parseNumberList(search.get("deviceType")),
    channelId: parseNumberList(search.get("channelId")),
    kind: parseNumberList(search.get("kind")),
  };
  const from = parseDate(search.get("from"));
  const to = parseDate(search.get("to"));
  const range = from && to ? { from, to } : null;
  const pageRaw = search.get("page");
  const page = pageRaw ? Math.max(1, parseInt(pageRaw, 10) || 1) : 1;
  return { filters, range, page };
};

export const buildDevicesUrlSearch = (
  filters: AnalyticsFilters,
  range: PickerRange,
  page: number,
): string => {
  const params = new URLSearchParams();
  for (const key of STRING_FILTER_KEYS) {
    const values = filters[key];
    if (values.length > 0) params.set(key, values.join(","));
  }
  for (const key of NUMBER_FILTER_KEYS) {
    const values = filters[key];
    if (values.length > 0) params.set(key, values.join(","));
  }
  params.set("from", range.from.toISOString());
  params.set("to", range.to.toISOString());
  if (page > 1) params.set("page", String(page));
  return params.toString();
};

export const createDeviceListRequest = (
  range: AnalyticsDateRange,
  filters: AnalyticsFilters,
  page: number,
  pageSize: number,
): DeviceListRequest => {
  const from = startOfDay(range.startDate);
  const to = addDays(startOfDay(range.endDate), 1);

  const filterPayload: DeviceListRequest["filters"] = {};

  if (filters.country.length > 0) filterPayload.country = [...filters.country];
  if (filters.region.length > 0) filterPayload.region = [...filters.region];
  if (filters.city.length > 0) filterPayload.city = [...filters.city];
  if (filters.platform.length > 0)
    filterPayload.platform = [...filters.platform];
  if (filters.deviceType.length > 0)
    filterPayload.deviceType = [...filters.deviceType];
  if (filters.channelId.length > 0)
    filterPayload.channelId = [...filters.channelId];
  if (filters.campaignUuid.length > 0)
    filterPayload.campaignUuid = [...filters.campaignUuid];
  if (filters.kind.length > 0) filterPayload.kind = [...filters.kind];
  if (filters.categories.length > 0)
    filterPayload.categories = [...filters.categories];

  return {
    createdAt: { from: from.toISOString(), to: to.toISOString() },
    page,
    pageSize,
    filters: filterPayload,
  };
};
